import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { bookingService, Booking } from '@/services/bookingService';
import { BookingFilter, UserRole } from '@shared/types/booking';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  X, 
  Calendar,
  User,
  CreditCard,
  MapPin,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Check,
  ChevronsUpDown,
  AlertTriangle,
  RefreshCw,
  DollarSign
} from 'lucide-react';
import SaudiRiyal from "@/components/shared/saudi-riyal";
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { BookingDetailsDialog } from './booking-details-dialog';
import { BookingStatusDialog } from './booking-status-dialog';

interface BookingListProps {
  userRole: UserRole;
  userId?: number;
  onViewBooking?: (booking: Booking) => void;
  onEditBooking?: (booking: Booking) => void;
}

export function BookingList({ userRole, userId, onViewBooking, onEditBooking }: BookingListProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<BookingFilter>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [propertySearch, setPropertySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  
  // Dropdown states
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<Array<{id: string, name: string}>>([]);
  const [availableProperties, setAvailableProperties] = useState<Array<{id: string, name: string}>>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  
  // Booking details dialog state
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  
  // Booking status dialog state
  const [statusDialogBookingId, setStatusDialogBookingId] = useState<string | null>(null);
  const [statusDialogCurrentStatus, setStatusDialogCurrentStatus] = useState<string>('');
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);

  // Load bookings
  const loadBookings = async () => {
    try {
      console.log('🔄 Loading bookings...', { userRole, userId, currentPage, pageSize });
      setLoading(true);
      
      // Build API parameters according to the specified format
      const apiParams: Record<string, string> = {
        page: currentPage.toString(),
        limit: pageSize.toString(),
      };

      // Add optional filters
      if (userRole === 'customer' && userId) {
        apiParams.userId = userId.toString();
      }
      
      if (filter.property_id) {
        apiParams.propertyId = filter.property_id.toString();
      }
      
      if (selectedUserId) {
        apiParams.userId = selectedUserId;
      }
      
      if (selectedPropertyId) {
        apiParams.propertyId = selectedPropertyId;
      }
      
      if (filter.date_from) {
        apiParams.start_date = filter.date_from;
      }
      
      if (filter.date_to) {
        apiParams.end_date = filter.date_to;
      }

      // Add search terms
      if (searchTerm.trim()) {
        apiParams.search = searchTerm.trim();
      }

      if (userSearch.trim()) {
        apiParams.userSearch = userSearch.trim();
      }

      if (propertySearch.trim()) {
        apiParams.propertySearch = propertySearch.trim();
      }

      // Add status filter
      if (filter.status) {
        apiParams.status = filter.status;
      }
      
      const response = await bookingService.viewBookings({ 
        filter: apiParams,
        page: currentPage,
        limit: pageSize
      });
      
      console.log('📊 Bookings API response:', response);
      console.log('📋 Bookings data:', response.data);
      console.log('📄 Pagination:', response.pagination);
      
      setBookings(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total);
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToLoadBookings'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load users for dropdown
  const loadUsers = async (searchQuery: string = '') => {
    // Temporarily allow all roles for testing
    // if (userRole !== 'admin' && userRole !== 'staff') return;
    
    try {
      setUsersLoading(true);
      const users = await bookingService.getUsers(searchQuery);
      
      // Ensure we always set an array
      if (Array.isArray(users)) {
        setAvailableUsers(users);
      } else {
        setAvailableUsers([]);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setAvailableUsers([]);
      toast({
        title: t('error') || 'خطأ',
        description: t('failedToLoadUsers') || 'فشل في تحميل المستخدمين',
        variant: 'destructive'
      });
    } finally {
      setUsersLoading(false);
    }
  };

  // Load properties for dropdown
  const loadProperties = async (searchQuery: string = '') => {
    // Temporarily allow all roles for testing
    // if (userRole !== 'admin' && userRole !== 'staff') return;
    
    try {
      setPropertiesLoading(true);
      const properties = await bookingService.getProperties(searchQuery);
      
      // Ensure we always set an array
      if (Array.isArray(properties)) {
        setAvailableProperties(properties);
      } else {
        setAvailableProperties([]);
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
      setAvailableProperties([]);
      toast({
        title: t('error') || 'خطأ',
        description: t('failedToLoadProperties') || 'فشل في تحميل العقارات',
        variant: 'destructive'
      });
    } finally {
      setPropertiesLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, [filter, currentPage, pageSize, searchTerm, userSearch, propertySearch, userRole, userId, selectedUserId, selectedPropertyId]);

  useEffect(() => {
    // Temporarily load for all roles for testing
    loadUsers();
    loadProperties();
  }, [userRole]);

  // Handle view booking details
  const handleViewBooking = (booking: Booking) => {
    setSelectedBookingId(booking.id);
    setIsDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setSelectedBookingId(null);
    setIsDetailsDialogOpen(false);
  };

  // Handle change booking status
  const handleChangeStatus = (booking: Booking) => {
    setStatusDialogBookingId(booking.id);
    setStatusDialogCurrentStatus(booking.status);
    setIsStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogBookingId(null);
    setStatusDialogCurrentStatus('');
    setIsStatusDialogOpen(false);
  };

  const handleStatusChanged = () => {
    // Reload bookings after status change
    loadBookings();
  };

  // Handle booking cancellation (deprecated - now uses status dialog)
  const handleCancelBooking = (booking: Booking) => {
    // Open status dialog with cancel pre-selected
    setStatusDialogBookingId(booking.id);
    setStatusDialogCurrentStatus(booking.status);
    setIsStatusDialogOpen(true);
  };

  // Handle booking deletion
  const handleDeleteBooking = async (bookingId: number) => {
    if (!confirm(t('confirmDeleteBooking'))) {
      return;
    }
    
    try {
      // In a real app, this would call a delete API endpoint
      // For now, we'll just show a success message
      toast({
        title: t('success'),
        description: t('bookingDeletedSuccessfully')
      });
      
      loadBookings();
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToDeleteBooking'),
        variant: 'destructive'
      });
    }
  };

  // Handle booking refund
  const handleRefundBooking = async (booking: Booking) => {
    const reason = prompt(t('enterRefundReason') || 'أدخل سبب الاسترداد:');
    if (!reason) {
      return;
    }
    
    if (!confirm(t('confirmRefundBooking') || 'هل أنت متأكد من استرداد هذا الحجز؟')) {
      return;
    }
    
    try {
      setLoading(true);
      await bookingService.refundBooking(booking.id, reason);
      
      toast({
        title: t('success') || 'نجح',
        description: t('bookingRefundedSuccessfully') || 'تم استرداد الحجز بنجاح'
      });
      
      // Reload bookings
      loadBookings();
    } catch (error) {
      console.error('Failed to refund booking:', error);
      toast({
        title: t('error') || 'خطأ',
        description: t('failedToRefundBooking') || 'فشل في استرداد الحجز',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle export
  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      // TODO: Implement export functionality
      toast({
        title: t('info'),
        description: t('exportFeatureComingSoon'),
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToExportBookings'),
        variant: 'destructive'
      });
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'confirmed': return 'default';
      case 'canceled': return 'destructive';
      case 'completed': return 'secondary';
      case 'pending': return 'outline';
      default: return 'outline';
    }
  };



  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>{t('filters')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Input
                placeholder={t('searchBookings') || 'البحث في الحجوزات'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            
            {/* User Filter Dropdown */}
            <div className="space-y-2">
              <div className="text-xs text-gray-500">{t('currentRole') || 'الدور الحالي'}: {userRole}</div>
              <div className="text-sm font-medium">{t('filterByUser') || 'فلترة حسب المستخدم'}</div>
              {(userRole === 'admin' || userRole === 'staff' || true) && (
                <Popover open={userDropdownOpen} onOpenChange={setUserDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={userDropdownOpen}
                      className="w-full justify-between"
                    >
                      {selectedUserId
                        ? availableUsers?.find((user) => user.id === selectedUserId)?.name
                        : (t('selectUser') || 'اختر المستخدم')}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput 
                        placeholder={t('searchUsers') || 'البحث عن المستخدمين'} 
                        onValueChange={loadUsers}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {usersLoading ? (t('loading') || 'جاري التحميل...') : (t('noUsersFound') || 'لم يتم العثور على مستخدمين')}
                        </CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value=""
                            onSelect={() => {
                              setSelectedUserId('');
                              setUserDropdownOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedUserId === '' ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {t('allUsers') || 'جميع المستخدمين'}
                          </CommandItem>
                          {Array.isArray(availableUsers) && availableUsers.map((user) => (
                            <CommandItem
                              key={user.id}
                              value={user.id}
                              onSelect={(currentValue) => {
                                setSelectedUserId(currentValue === selectedUserId ? '' : currentValue);
                                setUserDropdownOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedUserId === user.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="font-medium">{user.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            
            {/* Property Filter Dropdown */}
            <div className="space-y-2">
              <div className="text-sm font-medium">{t('filterByProperty') || 'فلترة حسب العقار'}</div>
              {(userRole === 'admin' || userRole === 'staff' || true) && (
                <Popover open={propertyDropdownOpen} onOpenChange={setPropertyDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={propertyDropdownOpen}
                      className="w-full justify-between"
                    >
                      {selectedPropertyId
                        ? availableProperties?.find((property) => property.id === selectedPropertyId)?.name
                        : (t('selectProperty') || 'اختر العقار')}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput 
                        placeholder={t('searchProperties') || 'البحث عن العقارات'} 
                        onValueChange={loadProperties}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {propertiesLoading ? (t('loading') || 'جاري التحميل...') : (t('noPropertiesFound') || 'لم يتم العثور على عقارات')}
                        </CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value=""
                            onSelect={() => {
                              setSelectedPropertyId('');
                              setPropertyDropdownOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedPropertyId === '' ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {t('allProperties') || 'جميع العقارات'}
                          </CommandItem>
                          {Array.isArray(availableProperties) && availableProperties.map((property) => (
                            <CommandItem
                              key={property.id}
                              value={property.id}
                              onSelect={(currentValue) => {
                                setSelectedPropertyId(currentValue === selectedPropertyId ? '' : currentValue);
                                setPropertyDropdownOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedPropertyId === property.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="font-medium">{property.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            
            <Select
              value={filter.status || 'all'}
              onValueChange={(value) => setFilter({ ...filter, status: value === 'all' ? undefined : value as any })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('selectStatus') || 'اختر الحالة'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allStatuses') || 'جميع الحالات'}</SelectItem>
                <SelectItem value="confirmed">{t('confirmed') || 'مؤكد'}</SelectItem>
                <SelectItem value="canceled">{t('canceled') || 'ملغي'}</SelectItem>
                <SelectItem value="completed">{t('completed') || 'مكتمل'}</SelectItem>
                <SelectItem value="pending">{t('pending') || 'في الانتظار'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                {t('dateFrom') || 'من تاريخ'}
              </label>
              <Input
                type="date"
                value={filter.date_from || ''}
                onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                {t('dateTo') || 'إلى تاريخ'}
              </label>
              <Input
                type="date"
                value={filter.date_to || ''}
                onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                {t('itemsPerPage') || 'عدد العناصر في الصفحة'}
              </label>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => {
                  setPageSize(parseInt(value));
                  setCurrentPage(1); // Reset to first page when changing page size
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 {t('items') || 'عناصر'}</SelectItem>
                  <SelectItem value="10">10 {t('items') || 'عناصر'}</SelectItem>
                  <SelectItem value="25">25 {t('items') || 'عناصر'}</SelectItem>
                  <SelectItem value="50">50 {t('items') || 'عناصر'}</SelectItem>
                  <SelectItem value="100">100 {t('items') || 'عناصر'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-between items-center mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setFilter({});
                setSearchTerm('');
                setUserSearch('');
                setPropertySearch('');
                setSelectedUserId('');
                setSelectedPropertyId('');
                setCurrentPage(1);
              }}
            >
              {t('clearFilters') || 'مسح الفلاتر'}
            </Button>
            
            {(userRole === 'admin' || userRole === 'staff') && (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => handleExport('csv')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('exportCSV') || 'تصدير CSV'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport('excel')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('exportExcel') || 'تصدير Excel'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('bookings') || 'الحجوزات'}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">{t('loading') || 'جاري التحميل'}...</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('bookingId') || 'رقم الحجز'}</TableHead>
                  <TableHead>{t('customer') || 'العميل'}</TableHead>
                  <TableHead>{t('property') || 'العقار'}</TableHead>
                  <TableHead>{t('checkIn') || 'تاريخ الوصول'}</TableHead>
                  <TableHead>{t('checkOut') || 'تاريخ المغادرة'}</TableHead>
                  <TableHead>{t('status') || 'الحالة'}</TableHead>
                  <TableHead>{t('totalAmount') || 'المبلغ الإجمالي'}</TableHead>
                  <TableHead>{t('actions') || 'الإجراءات'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      {t('noBookingsFound') || 'لم يتم العثور على حجوزات'}
                    </TableCell>
                  </TableRow>
                ) : (
                  bookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell className="font-medium">#{booking.id}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4" />
                          <span>{booking.customer?.name || 'Unknown Customer'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4" />
                          <span>{booking.property?.name || `Property #${booking.property_id}`}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4" />
                          <span>{booking.check_in ? format(new Date(booking.check_in), 'MMM dd, yyyy') : 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4" />
                          <span>{booking.check_out ? format(new Date(booking.check_out), 'MMM dd, yyyy') : 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(booking.status)}>
                          {t(booking.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          
                          <SaudiRiyal amount={booking.total_amount || 0} variant="new" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end space-x-1 min-w-[200px]">
                         
                          {/* View Action - Always visible */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewBooking(booking)}
                            title={t('view') || 'عرض'}
                            className="h-8 w-8 p-0 hover:bg-gray-100"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          
                          {/* Edit Action - Always show for testing (normally Staff/Admin only) */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditBooking?.(booking)}
                            title={t('edit') || 'تعديل'}
                            className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          
                          {/* Change Status Action - Always show for testing (normally Staff/Admin only) */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleChangeStatus(booking)}
                            title={t('changeStatus') || 'تغيير الحالة'}
                            className="h-8 w-8 p-0 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                          >
                            <AlertTriangle className="h-4 w-4" />
                          </Button>
                          
                          {/* Refund Action - Always show for testing (normally Admin only for paid bookings) */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRefundBooking(booking)}
                            title={t('refund') || 'استرداد'}
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          
                          {/* Delete Action - Always show for testing (normally Admin only) */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBooking(parseInt(booking.id))}
                            title={t('delete') || 'حذف'}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination Controls */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 px-2">
              <div className="text-sm text-muted-foreground">
                {t('showing') || 'عرض'} {((currentPage - 1) * pageSize) + 1} {t('to') || 'إلى'} {Math.min(currentPage * pageSize, totalItems)} {t('of') || 'من'} {totalItems} {t('results') || 'نتيجة'}
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('previous') || 'السابق'}
                </Button>
                
                <div className="flex items-center space-x-1">
                  {/* Show page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                >
                  {t('next') || 'التالي'}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Details Dialog */}
      <BookingDetailsDialog
        bookingId={selectedBookingId}
        isOpen={isDetailsDialogOpen}
        onClose={handleCloseDetailsDialog}
      />

      {/* Booking Status Dialog */}
      <BookingStatusDialog
        bookingId={statusDialogBookingId}
        currentStatus={statusDialogCurrentStatus}
        isOpen={isStatusDialogOpen}
        onClose={handleCloseStatusDialog}
        onStatusChanged={handleStatusChanged}
      />
    </div>
  );
}
